import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Chip,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { approveBudget, getBudget, getBudgetInsights, getBudgetReview } from '../services/budgetService';
import { useError } from '../contexts/ErrorContext';
import { colors } from '../styles';
import BudgetCategoryMiniCard from './BudgetCategoryMiniCard';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    maxWidth: '800px',
    width: '90%',
    maxHeight: '90vh'
  }
}));

const SummaryCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3)
}));

const BudgetReviewModal = ({
  open,
  onClose,
  budgetId,
  onApproved,
  onCategoryReview
}) => {
  const [budget, setBudget] = useState(null);
  const [insights, setInsights] = useState(null);
  const [categoryCleanupSuggestion, setCategoryCleanupSuggestion] = useState(null);
  const [approving, setApproving] = useState(false);
  const { showError } = useError();
  const navigate = useNavigate();

  const loadBudgetData = useCallback(async () => {
    if (!budgetId) return;

    try {
      // Load budget details and insights using authenticated API helpers
      const [budgetResponse, insightsResponse, reviewResponse] = await Promise.all([
        getBudget(budgetId),
        getBudgetInsights(budgetId).catch(err => {
          // If insights fail, continue with null insights
          console.warn('Failed to load insights, continuing without them:', err);
          return null;
        }),
        getBudgetReview(budgetId).catch(() => null)
      ]);

      const budgetData = budgetResponse?.data;
      if (budgetResponse?.success && budgetData) {
        setBudget(budgetData);
        setInsights(insightsResponse?.data ?? insightsResponse);
        const cleanup = reviewResponse?.data?.categoryCleanupSuggestion ?? null;
        setCategoryCleanupSuggestion(cleanup);
      } else {
        throw new Error(budgetResponse?.error || 'Failed to load budget');
      }
    } catch (error) {
      console.error('Error loading budget data:', error);
      showError('This budget is no longer available. It may have been deleted or is not active.');
      // Close the modal after a brief delay
      setTimeout(() => {
        onClose();
      }, 2000);
    }
  }, [budgetId, showError, onClose]);

  useEffect(() => {
    if (open && budgetId) {
      loadBudgetData();
    }
  }, [open, budgetId, loadBudgetData]);

  const handleApprove = async () => {
    if (!budget) return;

    setApproving(true);
    try {
      await approveBudget(budget._id);
      if (onApproved) {
        onApproved(budget);
      }
      onClose();
    } catch (error) {
      console.error('Error approving budget:', error);
      const code = error?.response?.data?.code;
      const msg = error?.response?.data?.error || 'Failed to approve budget';
      const txIds = error?.response?.data?.data?.transactionIds;
      if (code === 'LOW_CONFIDENCE_REVIEW_REQUIRED' && Array.isArray(txIds) && txIds.length > 0) {
        showError(msg);
        return;
      }
      showError(msg);
    } finally {
      setApproving(false);
    }
  };

  const handleCategoryReview = () => {
    if (onCategoryReview) {
      onCategoryReview(budget);
    } else {
      // Fallback: navigate to budgets page
      navigate('/budgets');
    }
    onClose();
  };

  const getBudgetHealthColor = () => {
    if (!insights) return colors.status.neutral;

    const totalBudget = insights.totalBudgeted || 0;
    const totalSpent = insights.totalSpent || 0;
    const remaining = totalBudget - totalSpent;

    if (remaining > totalBudget * 0.1) return colors.status.success; // >10% remaining
    if (remaining > 0) return colors.status.warning; // 0-10% remaining
    return colors.status.error; // Over budget
  };

  const getBudgetHealthText = () => {
    if (!insights) return 'Loading...';

    const totalBudget = insights.totalBudgeted || 0;
    const totalSpent = insights.totalSpent || 0;
    const remaining = totalBudget - totalSpent;
    const percentRemaining = totalBudget > 0 ? (remaining / totalBudget) * 100 : 0;

    if (remaining > totalBudget * 0.1) return `Healthy (${percentRemaining.toFixed(0)}% remaining)`;
    if (remaining > 0) return `Getting tight (${percentRemaining.toFixed(0)}% remaining)`;
    return `Over budget by $${Math.abs(remaining).toFixed(2)}`;
  };

  if (!budget) {
    return (
      <StyledDialog open={open} onClose={onClose}>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </DialogContent>
      </StyledDialog>
    );
  }

  const totalBudgeted = budget.categories?.reduce((sum, cat) => sum + (cat.amount || 0), 0) || 0;
  const totalSpent = insights?.totalSpent || 0;
  const totalTransactions = insights?.totalTransactions || 0;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 0, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {budget.name || 'Budget Review'}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Budget Summary */}
        <SummaryCard>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Budget Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h4" sx={{ fontWeight: 700, color: colors.primary }}>
                  ${totalBudgeted.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monthly Budget
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Typography variant="h4" sx={{ fontWeight: 700, color: getBudgetHealthColor() }}>
                  ${totalSpent.toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Spent This Period
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box textAlign="center">
                <Chip
                  label={getBudgetHealthText()}
                  color={totalSpent > totalBudgeted ? 'error' : 'success'}
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {totalTransactions} transactions
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </SummaryCard>

        {/* Categories Preview */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Category Breakdown
        </Typography>
        <Grid container spacing={2}>
          {budget.categories?.slice(0, 6).map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category._id}>
              <BudgetCategoryMiniCard
                category={category}
                budgetId={budget._id}
                compact
              />
            </Grid>
          ))}
          {budget.categories?.length > 6 && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                +{budget.categories.length - 6} more categories
              </Typography>
            </Grid>
          )}
        </Grid>

        {categoryCleanupSuggestion ? (
          <Alert
            severity="info"
            icon={<InfoIcon />}
            sx={{ mt: 3, borderRadius: '12px' }}
          >
            <Typography variant="body2">{categoryCleanupSuggestion.message}</Typography>
          </Alert>
        ) : null}

        {/* Action Guidance */}
        <Alert
          severity="info"
          icon={<InfoIcon />}
          sx={{ mt: 3, borderRadius: '12px' }}
        >
          <Typography variant="body2">
            Review your categories and amounts above. You can adjust individual categories
            or approve the budget as-is to start tracking your spending.
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 0, mt: 4, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={handleCategoryReview}
          startIcon={<EditIcon />}
          sx={{ flex: 1 }}
        >
          Review Categories
        </Button>
        <Button
          variant="contained"
          onClick={handleApprove}
          disabled={approving}
          startIcon={approving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          sx={{ flex: 1 }}
        >
          {approving ? 'Approving...' : 'Approve Budget'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default BudgetReviewModal;
